/** The one route table: every screen the shell can show, and where it sits in the information
 * architecture - **ADR-0212** (`docs/adr/0212-*.md`, build plan `docs/design/user-flow-ia-2026-11.md`),
 * which replaced the v5 stage F1 layout of eleven places.
 *
 * **Eighteen places under seven headings**, in the order of a bill's journey: Start (Home, Action
 * items, Ask), Collect (Upload, Processing), Emissions (Scope 1, 2, 3), Records (Documents, Vendors,
 * Facilities), Report (Filings, Reports, Dashboards), Plan (Targets & plan, Approvals & audit) and System
 * (Admin, Support). A heading is a label in the rail and never a page. **Admin is the company
 * Admin's alone** (KD, 2026-11-01); a person's own pages - My profile and My settings - are not in
 * the rail at all but in the avatar menu (`account`, below), each its own page. Under a place there is at
 * most **one strip of tabs**, and a screen may carry its own controls beneath that - two levels,
 * never three. Help is a tab of Support (and a help icon in the top bar), not an item.
 * **No place is added without an ADR** (ADR-0212 decision 11): a thing that is in neither KD's list
 * nor this table becomes a tab, a drawer, an icon in the top bar or a section of Settings.
 *
 * **Why a table and not the rail's list.** The rail, the strip of tabs, the breadcrumb's labels,
 * the command palette and the redirects were each about to read a different list of screens, and
 * a second list of screens is the copy that goes stale the day somebody adds a destination. The
 * defect `navItems.ts` has recorded since 2026-10-25 is the sentence *out of the rail means out of
 * the search too*; here it is a test (`routeTable.test.ts`): every route in the router is in this
 * table or in `NOT_IN_THE_SHELL`, and every row of this table is a route in the router.
 *
 * **Paths are stable.** Bookmarks, receipt links and the browser gate address screens by path, and
 * renaming forty paths for a new menu is churn with a security-relevant blast radius (a shared link
 * carries a record id). So the IA is a *mapping over the existing paths*, plus six new ones
 * (`/upload`, `/processing`, `/documents`, `/vendors`, `/facilities`, `/data-reports`). Filings keeps
 * `/reports` and `/readiness` (a link carries an obligation); Action items keeps `/approvals`.
 *
 * **A tab is a path, or a path and `?tab=`.** Where a place's tabs already had their own address
 * (`/notifications`, `/close`, `/ledger`) the tab is that address. Where the tabs are the views of
 * one screen (Processing's four) the tab is `?tab=` on the place's path, which is the convention
 * `useUrlTab` already uses: **the first tab is "absent"**, written as no parameter at all, so there
 * is one URL for the default view. `tabKey()` is the one spelling of a tab's identity.
 *
 * **Role-aware, and what that does and does not mean.** A role is offered the places it can use
 * (`roles`). That is presentation: the API still decides what may be read (a URL typed by hand
 * still reaches the screen, which says who can use it), and a control *inside* a screen that a role
 * cannot use is absent unless the role could act in another way (ADR-0212 decision 10).
 */

import type { Icon } from "@phosphor-icons/react";
import {
  Buildings,
  ChartBar,
  FileText,
  Files,
  Flame,
  House,
  Leaf,
  Lifebuoy,
  Lightning,
  ListChecks,
  ListMagnifyingGlass,
  ShieldCheck,
  ShieldStar,
  Sparkle,
  SquaresFour,
  Storefront,
  Truck,
  UploadSimple,
  UserCircle,
} from "@phosphor-icons/react";

/** The five roles the API issues (`api/auth.ts` `Me.role`). A sixth added there is *not offered
 * anything* here until it is listed, which is the safe default for a menu. */
export const ROLES = ["admin", "approver", "contributor", "viewer", "auditor"] as const;
export type Role = (typeof ROLES)[number];

const ALL: readonly Role[] = ROLES;
/** What the company Admin alone may use. */
export const ADMIN: readonly Role[] = ["admin"];
/** The roles that may lock a period (`api/reports.py`'s close routes): an Admin or an Approver. */
export const CLOSE_ROLES: readonly Role[] = ["admin", "approver"];
/** The roles that may put a reading in (`evidence.py` MAY_UPLOAD). A Viewer and an Auditor read
 * figures; they do not feed the inventory, so Add and Import are not offered to them either. */
export const UPLOAD_ROLES: readonly Role[] = ["admin", "approver", "contributor"];
/** The roles whose tabs of Approvals & audit include the ledger and Activity (`GET /ledger` and
 * `GET /activity` admit an Admin, an Approver and an Auditor; `whoCan.ts` holds the witness).
 * **A Contributor is not offered them** - the server refuses both reads, and a tab that opens to a
 * sentence in an empty panel is a place offered and then taken away (G-P6-08, ADR-0212 decision 10).
 * An Auditor is not offered them either, for the reason on the tabs below, though the server admits
 * the role: their reading of the chain cannot yet be narrowed to an engagement. */
const LEDGER_TABS: readonly Role[] = ["admin", "approver"];
export type NavId =
  | "home"
  | "actions"
  | "ask"
  | "upload"
  | "processing"
  | "scope1"
  | "scope2"
  | "scope3"
  | "documents"
  | "vendors"
  | "facilities"
  | "filings"
  | "reports"
  | "dashboards"
  | "targets"
  | "audit"
  | "admin"
  | "account"
  | "support";

/** The rail's seven groups, in order. Each has a name for a landmark group; **the first, Start, is not
 * printed** (KD's design review) and the other six are small labels. None is a page. */
export const RAIL_SECTIONS = [
  "Start",
  "Collect",
  "Emissions",
  "Records",
  "Report",
  "Plan",
  "System",
] as const;
export type RailSection = (typeof RAIL_SECTIONS)[number];

export interface NavSection {
  id: NavId;
  /** Where the rail entry goes: the place's first screen. */
  to: string;
  label: string;
  icon: Icon;
  section: RailSection;
  roles: readonly Role[];
  /** `false` for a place that is reached from somewhere other than the rail - the person's own pages,
   * from the avatar menu. It still lights, titles and appears in the palette; the rail does not draw
   * it. */
  rail?: boolean;
}

/** The eighteen in the rail, then the person's own pages (not in it). Order is the rail's order. */
export const NAV_SECTIONS: readonly NavSection[] = [
  { id: "home", to: "/", label: "Home", icon: House, section: "Start", roles: ALL },
  // The bell opens this place. What is empty for a role is absent from its tabs.
  { id: "actions", to: "/approvals", label: "Action items", icon: ListChecks, section: "Start", roles: ALL },
  { id: "ask", to: "/ask", label: "Ask", icon: Sparkle, section: "Start", roles: ALL },
  {
    id: "upload",
    to: "/upload",
    label: "Upload",
    icon: UploadSimple,
    section: "Collect",
    roles: UPLOAD_ROLES,
  },
  {
    id: "processing",
    to: "/processing",
    label: "Processing",
    icon: ListMagnifyingGlass,
    section: "Collect",
    roles: UPLOAD_ROLES,
  },
  { id: "scope1", to: "/scope-1", label: "Scope 1", icon: Flame, section: "Emissions", roles: ALL },
  { id: "scope2", to: "/scope-2", label: "Scope 2", icon: Lightning, section: "Emissions", roles: ALL },
  { id: "scope3", to: "/scope-3", label: "Scope 3", icon: Truck, section: "Emissions", roles: ALL },
  { id: "documents", to: "/documents", label: "Documents", icon: Files, section: "Records", roles: ALL },
  { id: "vendors", to: "/vendors", label: "Vendors", icon: Storefront, section: "Records", roles: ALL },
  {
    id: "facilities",
    to: "/facilities",
    label: "Facilities",
    icon: Buildings,
    section: "Records",
    roles: ALL,
  },
  { id: "filings", to: "/reports", label: "Filings", icon: FileText, section: "Report", roles: ALL },
  { id: "reports", to: "/data-reports", label: "Reports", icon: ChartBar, section: "Report", roles: ALL },
  {
    id: "dashboards",
    to: "/dashboards",
    label: "Dashboards",
    icon: SquaresFour,
    section: "Report",
    // A site lead reads their own bills, not a company view.
    roles: ["admin", "approver", "viewer", "auditor"],
  },
  {
    id: "targets",
    to: "/transition",
    label: "Targets & plan",
    icon: Leaf,
    section: "Plan",
    // An Auditor's work is the evidence behind a filing, not the plan beside it.
    roles: ["admin", "approver", "contributor", "viewer"],
  },
  {
    id: "audit",
    to: "/close",
    label: "Approvals & audit",
    icon: ShieldCheck,
    section: "Plan",
    // A Viewer has no queue and no ledger; the screens say so to anyone who arrives by link.
    roles: ["admin", "approver", "contributor", "auditor"],
  },
  // The company Admin's place: people and roles, approval routes, security, integrations, the
  // company, data, billing and usage, and the admin log. Offered to the Admin only.
  { id: "admin", to: "/admin", label: "Admin", icon: ShieldStar, section: "System", roles: ADMIN },
  { id: "support", to: "/support", label: "Support", icon: Lifebuoy, section: "System", roles: ALL },
  // Not in the rail: My profile and My settings live in the avatar menu, each its own page.
  {
    id: "account",
    to: "/my-settings",
    label: "My account",
    icon: UserCircle,
    section: "System",
    roles: ALL,
    rail: false,
  },
];

export interface Place {
  to: string;
  /** The screen's name, as the command palette lists it. */
  label: string;
  /** The label on the strip of tabs under the place's title; absent for a screen that is not a tab
   * (it is reached from elsewhere and highlights `under`'s tab instead). */
  tab?: string;
  /** The `?tab=` value that selects this tab on `to`. **Absent on a path's first tab**, whose URL
   * carries no parameter (`useUrlTab`'s convention). */
  param?: string;
  /** Other `?tab=` values that are views *inside* this tab and light it (Processing's Missing tab
   * holds the census screen, whose own views are `sources`, `coverage` and `billing`). They are not
   * tabs of their own, so the palette and the strip list none of them. */
  alsoParams?: readonly string[];
  nav: NavId;
  /** Narrower than the place's own roles: a tab that only some of the place's roles can use. */
  roles?: readonly Role[];
  /** For a screen that is not a tab: the tab (a `tabKey`) that stays lit while it is open. It is
   * still a palette entry, unlike a `CHILDREN` screen, because a person types its name. */
  under?: string;
}

/** A tab's identity: the path, plus `?tab=` when the tab is a view of a shared path. The one
 * spelling, used by `Located.tab`, `under` and `tabOf`. */
export function tabKey(place: { to: string; param?: string }): string {
  return place.param === undefined ? place.to : `${place.to}?tab=${place.param}`;
}

/** Every screen that is a stop in the shell, in tab order within its place. */
export const PLACES: readonly Place[] = [
  // Home
  { to: "/", label: "Home", nav: "home" },
  // Action items: the bell's destination. Tasks are folded into Waiting on you (P6 merges the
  // screens; the address still answers). Submitted by me and Decided are for the roles that submit.
  { to: "/approvals", label: "Action items", tab: "Waiting on you", nav: "actions" },
  {
    to: "/approvals",
    param: "submitted",
    label: "Submitted by me",
    tab: "Submitted by me",
    nav: "actions",
    roles: ["admin", "approver", "contributor"],
  },
  {
    to: "/approvals",
    param: "decided",
    label: "Decided",
    tab: "Decided",
    nav: "actions",
    // `GET /approval-requests/decided` admits the roles that decide; a Contributor submits and does not.
    roles: ["admin", "approver"],
  },
  { to: "/notifications", label: "Notifications", tab: "Notifications", nav: "actions" },
  { to: "/tasks", label: "Tasks & requests", nav: "actions", under: "/approvals" },
  // Ask
  { to: "/ask", label: "Ask", nav: "ask" },
  // Data in
  { to: "/upload", label: "Upload", nav: "upload" },
  { to: "/processing", label: "Processing", tab: "To review", nav: "processing" },
  {
    to: "/processing",
    param: "in-progress",
    label: "Processing: in progress",
    tab: "In progress",
    nav: "processing",
  },
  {
    to: "/processing",
    param: "missing",
    label: "Processing: missing",
    tab: "Missing",
    nav: "processing",
    // The census screen (its Sources, Coverage and Who bills what views) is part of Missing.
    alsoParams: ["sources", "coverage", "billing"],
  },
  {
    to: "/processing",
    param: "refused",
    label: "Processing: refused",
    tab: "Refused",
    nav: "processing",
  },
  // Emissions
  { to: "/scope-1", label: "Scope 1", tab: "Figures", nav: "scope1" },
  { to: "/scope-1", param: "month", label: "Scope 1: by month", tab: "By month", nav: "scope1" },
  {
    to: "/scope-1",
    param: "quality",
    label: "Scope 1: data quality",
    tab: "Data quality",
    nav: "scope1",
  },
  // The old inventory register and the factor library are no longer places of their own. They stay
  // screens, in the palette, lighting Scope 1 (ADR-0212 decision 8).
  { to: "/emissions", label: "All emissions", nav: "scope1", under: "/scope-1" },
  { to: "/factors", label: "Factor library", nav: "scope1", under: "/scope-1" },
  { to: "/scope-2", label: "Scope 2", tab: "Figures", nav: "scope2" },
  { to: "/scope-2", param: "month", label: "Scope 2: by month", tab: "By month", nav: "scope2" },
  {
    to: "/scope-2",
    param: "quality",
    label: "Scope 2: data quality",
    tab: "Data quality",
    nav: "scope2",
  },
  { to: "/scope-3", label: "Scope 3", tab: "Figures", nav: "scope3" },
  {
    to: "/scope-3",
    param: "categories",
    label: "Scope 3: categories",
    tab: "Categories",
    nav: "scope3",
  },
  {
    to: "/scope-3",
    param: "suppliers",
    label: "Scope 3: suppliers' share",
    tab: "Suppliers' share",
    nav: "scope3",
  },
  // Records
  { to: "/documents", label: "Documents", tab: "Library", nav: "documents" },
  { to: "/bulk", label: "Bulk edit", tab: "Bulk edit", nav: "documents" },
  { to: "/vendors", label: "Vendors", tab: "Vendors", nav: "vendors" },
  {
    to: "/vendors",
    param: "accounts",
    label: "Vendors: accounts",
    tab: "Accounts",
    nav: "vendors",
    roles: UPLOAD_ROLES,
  },
  {
    to: "/vendors",
    param: "requests",
    label: "Vendors: supplier requests",
    tab: "Supplier requests",
    nav: "vendors",
    roles: UPLOAD_ROLES,
  },
  { to: "/facilities", label: "Facilities", tab: "Sites", nav: "facilities" },
  {
    to: "/facilities",
    param: "companies",
    label: "Facilities: companies",
    tab: "Companies",
    nav: "facilities",
    // Adding a company is the Admin's (`POST /entities`); the Admin place's own tabs are the ones
    // marked with `ADMIN` itself, which a test counts.
    roles: ["admin"],
  },
  {
    to: "/facilities",
    param: "structure",
    label: "Facilities: structure",
    tab: "Structure",
    nav: "facilities",
  },
  // Out. Filings: one tab per supported regime is drawn by the Filings screen itself, from regime
  // data; a regime is never named in this table (ADR-0212 decision 6).
  { to: "/reports", label: "Filings", tab: "Filings", nav: "filings" },
  { to: "/prior-disclosures", label: "Past filings", tab: "Past filings", nav: "filings" },
  // The old readiness cockpit's address, now the determination behind each filing (ADR-0212, P10).
  { to: "/readiness", label: "Applicability", tab: "Applicability", nav: "filings" },
  { to: "/data-reports", label: "Reports", nav: "reports" },
  { to: "/dashboards", label: "Dashboards", tab: "Executive", nav: "dashboards" },
  {
    to: "/dashboards",
    param: "data-health",
    label: "Dashboards: data health",
    tab: "Data health",
    nav: "dashboards",
  },
  {
    to: "/dashboards",
    param: "operations",
    label: "Dashboards: operations",
    tab: "Operations",
    nav: "dashboards",
  },
  {
    to: "/dashboards",
    param: "suppliers",
    label: "Dashboards: suppliers",
    tab: "Suppliers",
    nav: "dashboards",
  },
  // Plan
  { to: "/transition", label: "Targets & plan", tab: "Targets", nav: "targets", roles: UPLOAD_ROLES },
  {
    to: "/transition",
    param: "recommendations",
    label: "Targets: recommendations",
    tab: "Recommendations",
    nav: "targets",
  },
  {
    to: "/transition",
    param: "initiatives",
    label: "Targets: initiatives",
    tab: "Initiatives",
    nav: "targets",
    roles: UPLOAD_ROLES,
  },
  { to: "/risks", label: "Climate risks", tab: "Climate risks", nav: "targets" },
  // The Auditor's first tab and the place they land after signing in (ADR-0212 decision 9). Read-only.
  { to: "/engagement", label: "Engagement", tab: "Engagement", nav: "audit", roles: ["auditor"] },
  { to: "/close", label: "Period close", tab: "Period close", nav: "audit", roles: CLOSE_ROLES },
  // The ledger and Activity cannot yet be narrowed to an engagement (an entry has no period or
  // company; ADR-0048, ADR-0213 proposed), so an Auditor is not offered them as tabs. A typed address
  // still answers and says why, and an Auditor who is not engaged finds the ledger from Engagement.
  { to: "/ledger", label: "Audit ledger", tab: "Audit ledger", nav: "audit", roles: LEDGER_TABS },
  { to: "/activity", label: "Activity", tab: "Activity", nav: "audit", roles: LEDGER_TABS },
  { to: "/assurance", label: "Assurance", tab: "Assurance", nav: "audit" },
  // System. **Admin** is eight tabs and the company Admin's alone: People and roles, Approval
  // routes, Security, Integrations, Company, Data, Billing and usage, and the Admin log. The server
  // refuses every Admin screen to a non-Admin (`MAY_ADMINISTER = (Role.ADMIN,)`), and each screen
  // answers a direct visit with the sentence naming whose decision it is rather than a form that 403s.
  // The screens that used to be tabs of Settings are still screens and still in the palette, and
  // light the Admin tab they belong to (`under`).
  { to: "/admin", label: "Admin", tab: "People and roles", nav: "admin", roles: ADMIN },
  {
    to: "/admin",
    param: "routes",
    label: "Admin: approval routes",
    tab: "Approval routes",
    nav: "admin",
    roles: ADMIN,
  },
  {
    to: "/admin",
    param: "security",
    label: "Admin: security",
    tab: "Security",
    nav: "admin",
    roles: ADMIN,
  },
  {
    to: "/admin",
    param: "integrations",
    label: "Admin: integrations",
    tab: "Integrations",
    nav: "admin",
    roles: ADMIN,
  },
  {
    to: "/admin",
    param: "company",
    label: "Admin: company",
    tab: "Company",
    nav: "admin",
    roles: ADMIN,
  },
  {
    to: "/admin",
    param: "data",
    label: "Admin: data",
    tab: "Data",
    nav: "admin",
    roles: ADMIN,
  },
  {
    to: "/admin",
    param: "billing",
    label: "Admin: billing and usage",
    tab: "Billing and usage",
    nav: "admin",
    roles: ADMIN,
  },
  {
    to: "/admin",
    param: "log",
    label: "Admin log",
    tab: "Admin log",
    nav: "admin",
    roles: ADMIN,
  },
  { to: "/people", label: "People and roles", nav: "admin", roles: ADMIN, under: "/admin" },
  {
    to: "/financials",
    label: "Financials & scope",
    nav: "admin",
    roles: ADMIN,
    under: "/admin?tab=company",
  },
  {
    to: "/settings/ai",
    label: "AI assistant",
    nav: "admin",
    roles: ADMIN,
    under: "/admin?tab=integrations",
  },
  {
    to: "/settings/billing",
    label: "Billing and usage",
    nav: "admin",
    roles: ADMIN,
    under: "/admin?tab=billing",
  },
  {
    to: "/audit",
    label: "Security history",
    nav: "admin",
    roles: ADMIN,
    under: "/admin?tab=security",
  },
  // The person's own pages: from the avatar menu, each its own page, no strip.
  { to: "/my-settings", label: "My settings", nav: "account" },
  { to: "/profile", label: "My profile", nav: "account" },
  { to: "/privacy-requests", label: "Your data requests", nav: "account" },
  { to: "/support", label: "Support", tab: "Support", nav: "support" },
  { to: "/help", label: "Help", tab: "Help", nav: "support" },
];

/** A screen reached from another: it belongs to a place and lights one of its tabs, and is not
 * itself a tab or a palette entry (an id in the address is not something a person types). */
export interface Child {
  match: RegExp;
  nav: NavId;
  /** The tab (a `tabKey`) that stays lit. */
  tabOf: string;
}

export const CHILDREN: readonly Child[] = [
  // A record is any kind of figure or thing. This is only the fallback while the page has not yet
  // learned which (it is read from the record): the page then says where it belongs with
  // `recordPlace`, and `locate` honours that. Scope 1 is the default, not the claim.
  { match: /^\/records\/[^/]+$/, nav: "scope1", tabOf: "/scope-1" },
  { match: /^\/factors\/[^/]+$/, nav: "scope1", tabOf: "/scope-1" },
  // A bill under review is a record with its document beside it; Processing is where it is reviewed.
  { match: /^\/evidence\/[^/]+$/, nav: "processing", tabOf: "/processing" },
  { match: /^\/evidence\/[^/]+\/import$/, nav: "documents", tabOf: "/documents" },
  { match: /^\/suppliers\/[^/]+$/, nav: "vendors", tabOf: "/vendors?tab=requests" },
  { match: /^\/sources\/[^/]+$/, nav: "processing", tabOf: "/processing?tab=missing" },
  { match: /^\/risks\/[^/]+$/, nav: "targets", tabOf: "/risks" },
  { match: /^\/ledger\/[^/]+$/, nav: "audit", tabOf: "/ledger" },
  { match: /^\/people\/[^/]+$/, nav: "admin", tabOf: "/admin" },
  { match: /^\/organisation\/sites\/[^/]+$/, nav: "facilities", tabOf: "/facilities" },
  { match: /^\/organisation\/structure$/, nav: "facilities", tabOf: "/facilities?tab=structure" },
];

/** Where a record page sits: the place it lights and the tab of that place that stays lit. */
export interface RecordPlace {
  nav: NavId;
  /** A `tabKey` of a tab of `nav`. */
  tab: string;
}

/** The place a record belongs to, by its kind (and, for an activity record, its scope).
 *
 * **A record page knows what it is**, and the shell does not: `/records/{id}` is one path for
 * thirteen kinds, so the address cannot say. Before this, every record lit Scope 1 and showed Scope 1's
 * tab strip - a Scope 2 record under "Scope 1" (live, release 05faf566). Each answer is the place whose
 * register lists that kind (`KIND_HOME`), and `tab` is one of that place's own tabs
 * (`routeTable.test.ts` asserts it, so a renamed tab cannot leave a record lighting nothing).
 *
 * `undefined` for a kind this build has not heard of: the fallback in `CHILDREN` then applies, which
 * is a wrong-but-visible place and not a blank rail.
 */
export function recordPlace(kind: string, scope?: string): RecordPlace | undefined {
  switch (kind) {
    case "activity_record": {
      const n = scope === "2" || scope === "3" ? scope : "1";
      return { nav: `scope${n}` as NavId, tab: `/scope-${n}` };
    }
    case "climate_risk":
      return { nav: "targets", tab: "/risks" };
    case "initiative":
      return { nav: "targets", tab: "/transition" };
    case "document":
      return { nav: "documents", tab: "/documents" };
    case "facility":
    case "meter":
      return { nav: "facilities", tab: "/facilities" };
    case "expected_source":
      return { nav: "processing", tab: "/processing?tab=missing" };
    case "supplier_request":
    case "supplier_submission":
    case "vendor":
    case "supply_account":
      return { nav: "vendors", tab: "/vendors" };
    case "filing_obligation":
    case "disclosure_statement":
      return { nav: "filings", tab: "/reports" };
    default:
      return undefined;
  }
}

/** Routes that are deliberately not in the shell: the signed-out screens (no rail, no session) and
 * the fallback. A route missing from here and from the table is the orphan the guard test fails on. */
export const NOT_IN_THE_SHELL: readonly string[] = ["/sign-in", "/join", "/reset-password", "$"];

/** Where an old address lands. `tab` is the `?tab=` value on the destination, absent for its first
 * tab. */
export interface Redirect {
  to: string;
  tab?: string;
}

/** The addresses that have no screen of their own, and where they land: the v5 vocabulary, and the
 * screens ADR-0212 moved into a new place. Query strings travel, because they carry the reporting
 * window and the Scope 2 basis, and a link that lost them would change which figures it opened.
 * Every detail path (`/evidence/{id}`, `/sources/{id}`, `/suppliers/{id}`, `/organisation/sites/{id}`)
 * keeps answering. */
export const REDIRECTS: Readonly<Record<string, Redirect>> = {
  "/coverage": { to: "/processing", tab: "missing" },
  "/figures": { to: "/emissions" },
  "/filings": { to: "/reports" },
  "/targets": { to: "/transition" },
  "/audit-trail": { to: "/ledger" },
  "/overview": { to: "/" },
  "/action-items": { to: "/approvals" },
  "/evidence": { to: "/documents" },
  "/sources": { to: "/processing", tab: "missing" },
  "/suppliers": { to: "/vendors", tab: "requests" },
  "/organisation": { to: "/facilities", tab: "companies" },
  // KD, 2026-11-01: Settings left the rail. `/settings` is the person's own settings page now, and
  // the company-admin content is the Admin place at `/admin`.
  "/settings": { to: "/my-settings" },
  "/administration": { to: "/admin" },
};

export interface Located {
  nav: NavSection;
  /** The tab that is lit (a `tabKey`), or `undefined` for a screen with no strip. */
  tab: string | undefined;
}

function sectionOf(id: NavId): NavSection {
  const found = NAV_SECTIONS.find((section) => section.id === id);
  if (!found) throw new Error(`routeTable: no nav section "${id}"`);
  return found;
}

/** The place a path (and, for a shared path, its `?tab=`) names. An unknown or carried tab value
 * falls back to the path's first tab, as `useUrlTab` does, so a `tab=register` carried from another
 * screen never lights nothing. */
function placeAt(pathname: string, tab: string | undefined): Place | undefined {
  const here = PLACES.filter((place) => place.to === pathname);
  if (here.length === 0) return undefined;
  return (
    here.find((place) => place.param !== undefined && place.param === tab) ??
    here.find((place) => tab !== undefined && (place.alsoParams ?? []).includes(tab)) ??
    here.find((place) => place.param === undefined) ??
    here[0]
  );
}

/** Where a path sits. `undefined` for a path the shell does not know (the fallback redirects it).
 *
 * `lights` is a page's own answer for a path that serves many places (`recordPlace`); it only
 * replaces a *child* screen's default, never a path that is itself a place. */
export function locate(
  pathname: string,
  tab?: string,
  lights?: RecordPlace,
): Located | undefined {
  const exact = placeAt(pathname, tab);
  if (exact) return { nav: sectionOf(exact.nav), tab: exact.tab ? tabKey(exact) : exact.under };
  const child = CHILDREN.find((candidate) => candidate.match.test(pathname));
  if (child && lights) return { nav: sectionOf(lights.nav), tab: lights.tab };
  if (child) return { nav: sectionOf(child.nav), tab: child.tabOf };
  return undefined;
}

/** The name of the tab a path (and its `?tab=`) lights, for a page that says which view it is. */
export function tabLabelAt(pathname: string, tab?: string): string | undefined {
  return placeAt(pathname, tab)?.tab;
}

export function canUse(role: string | undefined, roles: readonly Role[] | undefined): boolean {
  if (roles === undefined) return true;
  return role !== undefined && (roles as readonly string[]).includes(role);
}

/** The rail's entries for a role. **An unknown role (still reading `/auth/me`, or a role this table
 * has never heard of) is offered Home and Support only** - the places that cannot be wrong - rather
 * than everything, so the menu never shows a destination and then takes it away. */
export function navFor(role: string | undefined): readonly NavSection[] {
  const known = (ROLES as readonly string[]).includes(role ?? "");
  return NAV_SECTIONS.filter(
    (section) =>
      section.rail !== false &&
      (known ? canUse(role, section.roles) : section.id === "home" || section.id === "support"),
  );
}

/** The tabs under a place, for a role, in order. A place with one tab has no strip. */
export function tabsFor(nav: NavId, role: string | undefined): readonly Place[] {
  const section = sectionOf(nav);
  return PLACES.filter(
    (place) =>
      place.nav === nav && place.tab !== undefined && canUse(role, place.roles ?? section.roles),
  );
}

/** Where the rail takes a role: the place's first tab **that role is offered**. For most roles that is
 * `section.to`; for a role whose first tab is limited away (a Viewer has no Targets tab, an Auditor no
 * Period close) the rail would otherwise open a screen the strip does not list for them, and the
 * Auditor's "opens here" (ADR-0212) would land on a tab they cannot use. */
export function entryFor(
  section: NavSection,
  role: string | undefined,
): { to: string; tab: string | undefined } {
  const first = tabsFor(section.id, role)[0];
  return first ? { to: first.to, tab: first.param } : { to: section.to, tab: undefined };
}

/** Every screen a role can open from the palette, shaped like the old rail's entries. */
export function placesFor(role: string | undefined): readonly Place[] {
  // The rail's places, and the person's own pages (reached from the avatar menu, so always theirs).
  const offered = new Set<NavId>([...navFor(role).map((section) => section.id), "account"]);
  return PLACES.filter(
    (place) => offered.has(place.nav) && canUse(role, place.roles ?? sectionOf(place.nav).roles),
  );
}

export function labelOfPath(to: string): string | undefined {
  return PLACES.find((place) => place.to === to)?.label;
}

export function sectionById(id: NavId): NavSection {
  return sectionOf(id);
}

/** Whether a role may use the Add and Import actions of a data place (ADR-0212 decision 10). The
 * rule is the route table's own roles for entering data, so a control is never offered to a role the
 * server would refuse. */
export function mayEnterData(role: string | undefined): boolean {
  return canUse(role, UPLOAD_ROLES) && role !== undefined;
}
